import {
  type BulkManifestDiscrepanciesQuery,
  WasteManifestSource,
} from "generated-graphql/graphql";

import { DetailGrid, DetailRow } from "./discrepancyDetailBlocks";
import { type DiscrepancyStatus, hasResolvableAction } from "./DiscrepancyReviewDialog.helpers";
import { DiscrepancyRowExpanderShell } from "./DiscrepancyRowExpanderShell";
import { formatStoredFieldIssueValue } from "./fieldIssueValues";
import { SOURCE_LABEL } from "./manifestSourceDisplay";

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

/**
 * One source's value within a field issue, or nothing when that source holds
 * none. The stored value may be a JSON array, so it is read the way the Value
 * column reads it rather than printed as the raw string.
 */
function SourceRow({ label, value }: { label: string; value: string | null | undefined }) {
  const display = formatStoredFieldIssueValue(value);
  if (display == null) return null;
  return <DetailRow label={label} value={display} />;
}

export interface FieldIssueRowExpanderProps {
  discrepancy: Discrepancy;
  status: DiscrepancyStatus;
  open: boolean;
  colCount: number;
  /** Every row action here is staff-only; the row still renders values and history for a customer. */
  isStaff: boolean;
  onResolve: (discrepancy: Discrepancy) => void;
  onMarkUnresolved: (discrepancy: Discrepancy) => void;
  onUndoResolution: (discrepancy: Discrepancy) => void;
  mutationLoading?: boolean;
}

/**
 * A Data Quality Issues row's expanded detail: the status explanation, its
 * full per-source values, the resolve actions the row's old kebab menu used
 * to carry, and its event history.
 */
export function FieldIssueRowExpander({
  discrepancy,
  status,
  open,
  colCount,
  isStaff,
  onResolve,
  onMarkUnresolved,
  onUndoResolution,
  mutationLoading,
}: FieldIssueRowExpanderProps) {
  return (
    <DiscrepancyRowExpanderShell
      discrepancy={discrepancy}
      status={status}
      open={open}
      colSpan={colCount}
      isStaff={isStaff}
      canResolve={hasResolvableAction(discrepancy, false)}
      isMutating={mutationLoading === true}
      onResolve={() => onResolve(discrepancy)}
      onMarkUnresolved={() => onMarkUnresolved(discrepancy)}
      onUndoResolution={() => onUndoResolution(discrepancy)}
    >
      <DetailGrid>
        <SourceRow label="e-Manifest" value={discrepancy.eManifestValue} />
        <SourceRow label="Vendor" value={discrepancy.vendorValue} />
        <SourceRow
          label={SOURCE_LABEL[WasteManifestSource.ManifestDocument]}
          value={discrepancy.manifestDocumentValue}
        />
      </DetailGrid>
    </DiscrepancyRowExpanderShell>
  );
}
